import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Globe, Plus, X } from 'lucide-react';
import { SUGGESTED_NTP_SERVERS, parseNtpServers, type NtpSettings } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { useSession } from '../../lib/session';
import { TimezoneSelect } from '../../components/TimezoneSelect';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../../components/ui';

interface NtpSettingsResponse {
  own: NtpSettings;
  effective: NtpSettings;
  scope: string;
}

/**
 * The time servers the platform keeps once.
 *
 * Every OLT already carries the servers it should ask, and every one of them
 * was typed in by hand at onboarding. The failure mode of typing something in
 * by hand four times is not four typos — it is the one chassis where somebody
 * was interrupted and never came back. That OLT keeps a clock that looks
 * perfectly right, and nothing complains, until a year of its event
 * timestamps has to be read alongside another chassis's and cannot be.
 *
 * So it is kept here once, and onboarding applies it. A per-OLT list still
 * overrides it — a chassis behind a management network that cannot route to
 * the public internet needs its own answer — but nobody has to remember the
 * common case.
 *
 * The timezone is here for the same reason and behaves differently on purpose:
 * it is the DEFAULT a new OLT is recorded with, and recording is as far as it
 * goes. Nothing on this page writes a timezone to a chassis. See the note on
 * the OLT's Time card.
 */
export function NtpTab() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canManage = can('settings.manage');

  const query = useQuery({
    queryKey: ['settings', 'ntp'],
    queryFn: () => api.get<NtpSettingsResponse>('/settings/ntp'),
  });

  const [servers, setServers] = useState('');
  const [timezone, setTimezone] = useState<string | null>(null);
  const [zteInterval, setZteInterval] = useState('');
  const [huaweiInterval, setHuaweiInterval] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Loaded once the server answers, and not re-imposed on every refetch: an
  // operator halfway through typing a server list should not have it replaced
  // under them by a background poll.
  const loadedFor = query.data?.own.updatedAt ?? null;
  useEffect(() => {
    const own = query.data?.own;
    if (!own) return;
    setServers(own.servers.join('\n'));
    setTimezone(own.timezone);
    setZteInterval(own.ztePollInterval === null ? '' : String(own.ztePollInterval));
    setHuaweiInterval(own.huaweiSyncInterval === null ? '' : String(own.huaweiSyncInterval));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedFor, query.data?.scope]);

  const save = useMutation({
    mutationFn: () =>
      api.put<NtpSettings>('/settings/ntp', {
        servers: parseNtpServers(servers),
        timezone,
        ztePollInterval: zteInterval.trim() ? Number(zteInterval) : null,
        huaweiSyncInterval: huaweiInterval.trim() ? Number(huaweiInterval) : null,
      }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings', 'ntp'] });
    },
    onError: (err) => {
      setSaved(false);
      setError(err instanceof Error ? err.message : 'Could not save the time servers');
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  function addSuggestion(host: string) {
    const current = parseNtpServers(servers);
    if (current.includes(host)) return;
    setSaved(false);
    setServers([...current, host].join('\n'));
  }

  const listed = parseNtpServers(servers);
  const effective = query.data?.effective;
  const inherited = effective?.source === 'platform' && query.data?.scope !== effective.scope;

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Clock className="size-4 text-muted" />
            Time servers
            {query.data?.own.servers.length ? (
              <Pill tone="success">{query.data.own.servers.length} set</Pill>
            ) : (
              <Pill tone="warn">none set</Pill>
            )}
          </span>
        }
      >
        <form onSubmit={onSubmit}>
          <div className="space-y-4 p-5">
            <p className="text-sm text-muted">
              A new OLT is pointed at these when it is onboarded, and any OLT can be re-pointed at
              them from its Time card. A chassis with no time source keeps a clock that looks right
              today — the cost arrives months later, when every event and optical reading it
              recorded carries the wrong timestamp and no two OLTs' logs can be lined up. A name
              that will not resolve is reported here as a name that will not resolve, rather than
              sent to a chassis to come back as a parse error.
            </p>

            {/* One line, not a redesign. A name is the right thing to keep
                here — it is what an operator knows and it survives a pool
                renumbering — but no OLT in this fleet accepts one, and a field
                that silently takes a hostname which is resolved somewhere else
                is a field that will be blamed for the next refusal. */}
            <Field
              label="NTP servers"
              hint="One per line, or comma-separated. ZTE asks them in the order given — up to eight. A name such as time.google.com is fine: no OLT accepts a hostname, so SimpleACS resolves it and sends the address — one address per entry, preferring one the chassis already has."
            >
              <textarea
                className="input min-h-[6rem] font-mono text-xs"
                value={servers}
                disabled={!canManage}
                onChange={(e) => {
                  setSaved(false);
                  setServers(e.target.value);
                }}
                placeholder="time.google.com"
              />
            </Field>

            {canManage && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_NTP_SERVERS.map((s) => {
                  const already = listed.includes(s.host);
                  return (
                    <button
                      key={s.host}
                      type="button"
                      title={s.label}
                      disabled={already}
                      onClick={() => addSuggestion(s.host)}
                      className={
                        already
                          ? 'flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs text-faint'
                          : 'flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs text-muted hover:border-primary hover:text-primary'
                      }
                    >
                      {already ? <X className="size-3" /> : <Plus className="size-3" />}
                      {s.host}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Two fields rather than one, because they are two units. ZTE's
                  poll-interval is a power-of-two exponent and Huawei's
                  sync-interval is seconds; a single shared number would mean
                  different things depending on which chassis read it. */}
              <Field
                label="ZTE poll interval (exponent)"
                hint="4–17, a power of two in seconds. Blank uses 10, about 17 minutes."
              >
                <Input
                  value={zteInterval}
                  disabled={!canManage}
                  onChange={(e) => {
                    setSaved(false);
                    setZteInterval(e.target.value);
                  }}
                  placeholder="10"
                />
              </Field>
              <Field
                label="Huawei sync interval (seconds)"
                hint="64–1024. Blank uses the chassis default of 600."
              >
                <Input
                  value={huaweiInterval}
                  disabled={!canManage}
                  onChange={(e) => {
                    setSaved(false);
                    setHuaweiInterval(e.target.value);
                  }}
                  placeholder="600"
                />
              </Field>
            </div>

            <Field
              label="Default timezone"
              hint="Recorded on every OLT onboarded from here. Nothing writes it to a chassis on its own — see the note below."
            >
              <TimezoneSelect
                value={timezone}
                onChange={(zone) => {
                  setSaved(false);
                  setTimezone(zone);
                }}
              />
            </Field>

            {inherited && effective && (
              <p className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-muted">
                This organization has no set of its own, so it uses the platform's:{' '}
                <span className="text-ink">{effective.servers.join(', ') || 'nothing set'}</span>.
                Saving here gives it one.
              </p>
            )}

            {error && <ErrorNotice error={new Error(error)} />}
            {saved && !error && (
              <p className="text-sm text-success">
                Saved. Existing OLTs keep the servers they already have — re-point one from its
                Time card.
              </p>
            )}
          </div>
          {canManage && (
            <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
              <span className="text-xs text-faint">
                {query.data?.own.updatedAt
                  ? `Last changed ${formatRelative(query.data.own.updatedAt)}${
                      query.data.own.updatedBy ? ` by ${query.data.own.updatedBy}` : ''
                    }`
                  : 'Never set'}
              </span>
              <Button type="submit" variant="primary" loading={save.isPending}>
                Save
              </Button>
            </footer>
          )}
        </form>
      </Card>

      <Card
        title={
          <span className="flex items-center gap-2">
            <Globe className="size-4 text-muted" />
            Why the timezone is not pushed
          </span>
        }
      >
        <div className="space-y-3 p-5 text-sm text-muted">
          <p>
            An OLT's timezone does two things. It is the clock that OLT's <em>schedules</em> are
            read in — its nightly config backup and drift check run at the configured hour where
            the chassis is, so a chassis in Accra backs up at 02:00 Accra time even when this
            server is in Nairobi. Separately, the zone can be written to the chassis itself from
            its Time card, and there the two vendors are in different places.
          </p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li>
              <span className="text-ink">ZTE</span> — captured.{' '}
              <code className="font-mono text-xs">clock timezone ?</code> on a live C300 gives the
              zone name, then a signed hours offset, then optional minutes — so{' '}
              <code className="font-mono text-xs">clock timezone Africa/Nairobi 3</code> is the
              chassis's own grammar and goes without a confirmation.
            </li>
            <li>
              <span className="text-ink">Huawei</span> — its running config prints{' '}
              <code className="font-mono text-xs">timezone GMT+ 03:00</code>. A running config is a
              set of commands the chassis accepted, which is good evidence, but that line has not
              been re-entered to prove the printed form is also the input form. Writing it still
              needs an explicit confirmation.
            </li>
          </ul>
          <p>
            Every up-time, down-time, down-cause and optical reading an OLT reports is stamped with
            its own clock. A timezone an hour out puts a subscriber's outage in the wrong part of
            the day on every chassis that took it, and nothing anywhere complains.
          </p>
        </div>
      </Card>
    </div>
  );
}
